import type { Person } from '@local/common-lib';
import { captureException, captureMessage } from '@sentry/react';
import type { FC } from 'react';
import type { RouteObject } from 'react-router-dom';
import { createHashRouter, useRouteError } from 'react-router-dom';
import { Layout } from './layout.js';
import { EditDeviceSettingsPage } from './pages/device-settings/edit.controller.js';
import { ErrorPage } from './pages/error/index.js';
import { IncidentMapPage } from './pages/incident-map/index.js';
import { NotAuthenticatedPage } from './pages/not-authenticated/page.js';
import { NotFoundPage } from './pages/not-found/index.js';
import {
  CreateOrganizationPage,
  EditOrganizationPage,
} from './pages/organization/index.js';
import { OrganizationListPage } from './pages/organization-list/index.js';
import { SetPasswordPage } from './pages/password/set-password.js';
import { EditPatrolPage } from './pages/patrol/edit.controller.js';
import {
  CreatePatrolPage,
  DisplayPatrolPage,
  EccLogOnPage,
  QuickPatrolPage,
  RecordPatrolPage,
} from './pages/patrol/index.js';
import { AcceptDeclinePageEmailEntry } from './pages/patrol-accept-decline/index.js';
import {
  PastPatrolListPage,
  ScheduledPatrolListPage,
} from './pages/patrol-list/index.js';
import {
  CreatePersonPage,
  DisplayPersonPage,
  EditPersonPage,
} from './pages/person/index.js';
import { PersonListPage } from './pages/person-list/index.js';
import { MyProfilePage } from './pages/profile/my-profile.js';
import { MyRosterPage } from './pages/profile/my-roster.js';
import { SecurityPage } from './pages/profile/security.js';
import { ReportsPage } from './pages/reports/index.js';
import {
  CreateVehicleContent,
  EditVehicleContent,
} from './pages/vehicle/forms.js';
import { VehicleListPage } from './pages/vehicle/index.js';
import { useAuth } from './utils/auth/auth.js';
import { AuthStatus, type UserInfo } from './utils/auth/index.js';

interface RouteProps {
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  component: FC<any>;
  path: string;
}

const evaluateRoutes = (
  userInfo: UserInfo,
  items: [RouteProps, Person.Role[]][],
): RouteObject[] => {
  return items
    .filter(([, roles]) => userInfo.hasRole(...roles))
    .map(([route]) => ({
      Component: route.component,
      ErrorBoundary,
      path: route.path,
    }));
};

export const ErrorBoundary: FC = () => {
  const error = useRouteError();
  if (error instanceof Error) {
    captureException(error);
  } else {
    captureMessage('Page routing error', { level: 'error' });
  }
  console.error(error);
  return <ErrorPage error={error} />;
};

export const useRouter = (): ReturnType<typeof createHashRouter> => {
  const auth = useAuth();

  if (auth.state?.status !== AuthStatus.AUTHENTICATED) {
    return createHashRouter([
      {
        Component: SetPasswordPage,
        ErrorBoundary,
        path: '/set-password/:token',
      },
      {
        Component: AcceptDeclinePageEmailEntry,
        ErrorBoundary,
        path: '/assignment/confirm/:token',
      },
      {
        Component: NotAuthenticatedPage,
        ErrorBoundary,
        path: '*',
      },
    ]);
  }

  const { userInfo } = auth.state;
  if (userInfo.isSuperAdmin) {
    return createHashRouter([
      {
        children: [
          {
            Component: OrganizationListPage,
            ErrorBoundary,
            path: '/',
          },
          {
            Component: CreateOrganizationPage,
            ErrorBoundary,
            path: '/organization/new',
          },
          {
            Component: EditOrganizationPage,
            ErrorBoundary,
            path: '/organization/:id',
          },
          {
            Component: SecurityPage,
            ErrorBoundary,
            path: '/security',
          },
          {
            Component: NotFoundPage,
            ErrorBoundary,
            path: '*',
          },
        ],
        ErrorBoundary,
        element: <Layout />,
        path: '/',
      },
    ]);
  }

  const isPatroller = userInfo.hasRole('patroller');

  const paths = [
    {
      Component: isPatroller ? MyRosterPage : MyProfilePage,
      ErrorBoundary,
      path: '/',
    },
    ...evaluateRoutes(userInfo, [
      [
        { component: MyProfilePage, path: '/profile' },
        ['admin', 'watcher', 'patroller'],
      ],
      [
        { component: SecurityPage, path: '/security' },
        ['admin', 'watcher', 'patroller'],
      ],
      [{ component: EditOrganizationPage, path: '/organization' }, ['admin']],
      [{ component: EditDeviceSettingsPage, path: '/device' }, ['admin']],
      [{ component: ReportsPage, path: '/reports' }, ['admin']],
      [{ component: VehicleListPage, path: '/vehicles' }, ['admin']],
      [{ component: CreateVehicleContent, path: '/vehicle/new' }, ['admin']],
      [
        { component: EditVehicleContent, path: '/vehicle/edit/:rego' },
        ['admin'],
      ],
      // PATROLS
      [
        { component: PastPatrolListPage, path: '/patrols/past' },
        ['admin', 'watcher', 'patroller'],
      ],
      [
        { component: ScheduledPatrolListPage, path: '/patrols/scheduled' },
        ['admin', 'watcher', 'patroller'],
      ],
      [{ component: CreatePatrolPage, path: '/patrol/new' }, ['admin']],
      [
        { component: QuickPatrolPage, path: '/patrol/quick-patrol' },
        ['patroller'],
      ],
      [
        { component: DisplayPatrolPage, path: '/patrol/:id' },
        ['admin', 'watcher', 'patroller'],
      ],
      [{ component: EditPatrolPage, path: '/patrol/:id/edit' }, ['admin']],
      [
        { component: EccLogOnPage, path: '/patrol/:id/ecc-log-on' },
        ['patroller'],
      ],
      [
        { component: RecordPatrolPage, path: '/patrol/:id/record' },
        ['patroller'],
      ],
      [
        { component: IncidentMapPage, path: '/incidents' },
        ['admin', 'watcher', 'patroller'],
      ],
      [
        { component: IncidentMapPage, path: '/activity-map' },
        ['admin', 'watcher', 'patroller'],
      ],
      // PERSONS
      [{ component: PersonListPage, path: '/people' }, ['admin', 'watcher']],
      [{ component: CreatePersonPage, path: '/person/new' }, ['admin']],
      [
        { component: DisplayPersonPage, path: '/person/:id' },
        ['admin', 'watcher'],
      ],
      [{ component: EditPersonPage, path: '/person/:id/edit' }, ['admin']],
    ]),
  ];

  return createHashRouter([
    {
      Component: AcceptDeclinePageEmailEntry,
      ErrorBoundary,
      path: '/assignment/confirm/:token',
    },
    {
      children: [
        ...paths,
        {
          Component: NotFoundPage,
          ErrorBoundary,
          path: '*',
        },
      ],
      ErrorBoundary,
      element: <Layout />,
      path: '/',
    },
  ]);
};
